import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SectionHeading from "@/components/np/SectionHeading";
import BookButton from "@/components/np/BookButton";
import Reveal from "@/components/np/Reveal";
import { conditionThemes, doctor, memberships, treatments } from "@/content";

export const WhatWeTreat = () => (
  <section className="section" aria-labelledby="what-title">
    <div className="container-np grid gap-10 lg:grid-cols-12">
      <SectionHeading id="what-title" className="lg:col-span-5" eyebrow="Vad vi behandlar" title="Smärta i muskler, leder, ledband och nerver" intro="Vi tar emot patienter med långvariga besvär – ofta sådana som funnits länge eller kvarstår efter skada, operation eller graviditet." />
      <div className="lg:col-span-7">
        <ul className="flex flex-wrap gap-2.5" data-testid="condition-themes">
          {conditionThemes.map((c) => <li key={c} className="rounded-full bg-white px-4 py-2 text-[15px] text-ink ring-1 ring-line">{c}</li>)}
        </ul>
        <Link to="/besvar" data-testid="what-we-treat-link" className="link-arrow mt-8">Se alla besvär per kroppsområde <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  </section>
);

export const DoctorSection = () => (
  <section className="section bg-sand/60" aria-labelledby="doctor-title">
    <div className="container-np grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
      <Reveal className="lg:col-span-5">
        <div className="overflow-hidden rounded-[32px] bg-pine">
          <img src={doctor.secondaryPhoto || doctor.photo} alt={doctor.photoAlt} loading="lazy" width="1024" height="910" className="aspect-[4/5] w-full object-cover object-[50%_25%]" />
        </div>
      </Reveal>
      <div className="lg:col-span-7">
        <SectionHeading id="doctor-title" eyebrow="Din läkare" title={`Du träffar ${doctor.name}`} intro={doctor.bio[0]} />
        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
          {doctor.facts.map((f) => <li key={f} className="rounded-2xl bg-white p-4 text-[15px] text-ink ring-1 ring-line">{f}</li>)}
        </ul>
        <p className="mt-8 text-sm font-semibold text-ink">Medlem i</p>
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
          {memberships.map((m) => (
            <li key={m.name}><a href={m.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-pine underline decoration-pine/30 underline-offset-4 hover:decoration-pine">{m.name}<ArrowUpRight className="h-3.5 w-3.5" /></a></li>
          ))}
        </ul>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
          <BookButton testId="doctor-book-button" />
          <Link to="/om-nordic-prolo" data-testid="doctor-read-more-link" className="link-arrow sm:ml-4">Mer om Dr Hivar <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
    </div>
  </section>
);

export const TreatmentsCompact = () => (
  <section className="section" aria-labelledby="treat-title">
    <div className="container-np">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <SectionHeading id="treat-title" eyebrow="Behandlingar" title="Metoder vi arbetar med" />
        <Link to="/behandlingar" data-testid="treatments-all-link" className="link-arrow">Alla behandlingar <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <ul className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {treatments.map((t, i) => (
          <Reveal as="li" key={t.id} delay={i * 60} className={i === 0 ? "lg:row-span-2" : ""}>
            <Link to={`/behandlingar#${t.id}`} data-testid={`treatment-card-${t.id}`} className={`group flex h-full flex-col rounded-[28px] p-7 ring-1 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl ${i === 0 ? "bg-pine text-cream ring-pine" : "bg-white text-ink ring-line"}`}>
              <span className={`font-serif text-lg ${i === 0 ? "text-amber" : "text-pine"}`}>{String(i + 1).padStart(2, "0")}</span>
              <h3 className={`mt-3 font-serif ${i === 0 ? "text-4xl" : "text-2xl"}`}>{t.name}</h3>
              <p className={`mt-3 text-[15px] leading-relaxed ${i === 0 ? "text-cream/80" : "text-muted"}`}>{t.short}</p>
              <span className={`mt-auto inline-flex items-center gap-1 pt-6 text-sm font-semibold ${i === 0 ? "text-amber" : "text-pine"}`}>Läs mer <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
            </Link>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);
